/** Standard ERC-20 `totalSupply()`. */
export const TotalSupplyAbi = [
  {
    name: 'totalSupply',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
] as const

/** Standard ERC-20 `balanceOf(address)`. */
export const BalanceOfAbi = [
  {
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'address', name: 'account' }],
    outputs: [{ type: 'uint256' }],
  },
] as const

/** ERC-4626 read surface — `totalAssets()` + `convertToAssets(uint256)`. */
export const Erc4626ReadAbi = [
  {
    name: 'totalAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'convertToAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256', name: 'shares' }],
    outputs: [{ type: 'uint256', name: 'assets' }],
  },
] as const

/**
 * StakedUSDeV2's governance-mutable exit lock. `uint24` seconds, capped by the
 * vault's own `MAX_COOLDOWN_DURATION` (90 days on every clone we carry).
 *
 * Worth reading rather than pinning because the two can disagree by orders of
 * magnitude: Brix's wiTRY documents a 3-day cooldown and answered **300** on
 * 2026-08-14, a pre-launch placeholder the owner can move at any time with no
 * timelock. A registry constant would have advertised one of those two numbers
 * and been wrong about the other.
 */
/**
 * Theo `sthUSD`'s exit lock, read live for the same reason as
 * `cooldownDuration` above: `setLockupPeriod` is `onlyOwner` with no timelock
 * and `MAX_LOCKUP_PERIOD` allows **30 days** against the 5 days configured
 * today, so a registry pin is a snapshot of a dial that can move six-fold.
 *
 * Semantically it is not quite the StakedUSDe cooldown — it starts at
 * `initiateRedeem`, not at a separate `cooldown` call — but it answers the
 * same question the row asks ("how long is the money stuck after you ask for
 * it"), so it feeds the same `withdrawalCooldownSeconds`.
 */
export const LockupPeriodAbi = [
  {
    name: 'lockupPeriod',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
] as const

export const CooldownDurationAbi = [
  {
    name: 'cooldownDuration',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint24' }],
  },
] as const

/**
 * Wren's self-priced NAV token (`wstGBP`). All three are plain `uint256`
 * getters on the share token itself — there is no `asset()`, no
 * `totalAssets()` and no `convertTo*`, so this IS the whole value surface.
 *
 *  - `navprice()` — the restated NAV, 1e18 underlying per share, monotonic.
 *  - `burncost()` — what a redemption actually pays, 1e18. Sits BELOW the NAV
 *    (a ~25 bps haircut measured 2026-08-14), so the two are not
 *    interchangeable and the gap is reported rather than smoothed away.
 *  - `burnable()` — 0/1 switch on the redemption path.
 */
export const WrenNavReadAbi = [
  {
    name: 'navprice',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'burncost',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'burnable',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
] as const

/**
 * Chainlink-shaped NAV feed — the only getter we need from Re Protocol's
 * per-chain reUSD/reUSDe price oracle. `answer` is 18-decimal USD per
 * share and is re-published daily at 00:00 UTC. `roundId` /
 * `answeredInRound` are always 0 on these feeds (no round history), so
 * only `answer` is load-bearing.
 */
export const NavOracleReadAbi = [
  {
    name: 'latestRoundData',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [
      { name: 'roundId', type: 'uint80' },
      { name: 'answer', type: 'int256' },
      { name: 'startedAt', type: 'uint256' },
      { name: 'updatedAt', type: 'uint256' },
      { name: 'answeredInRound', type: 'uint80' },
    ],
  },
] as const
